/**
 * procedural-terrain.ts — generates a stage-1 heightmap.
 *
 * Conventions:
 *  - Scene extent: 2 km × 2 km centered at world origin (X,Z in [-1000, +1000]).
 *  - Heightmap resolution: HEIGHTMAP_RES × HEIGHTMAP_RES (default 1024).
 *  - Height stored in metres. Land is positive, lakebed is negative.
 *  - water_level = 0 by default.
 *
 * Stage 1 shape: a roughly circular basin in the centre with a Gaussian
 * depression (max depth ~5 m, diameter ~600 m). Surrounding land has gentle
 * rolling hills (small-amplitude value noise). Edges of the scene are slightly
 * lifted to suggest distant terrain.
 */

export const SCENE_EXTENT_M = 2000;
export const HEIGHTMAP_RES = 1024;

export interface TerrainHeightmap {
  /** Single-channel float32 heights (metres). Length = res*res. Row-major (y outer, x inner). */
  data: Float32Array;
  resolution: number;
  /** World-space scene extent in metres (X = Z extent; square). */
  extent: number;
}

export function generateProceduralTerrain(
  res: number = HEIGHTMAP_RES,
  extent: number = SCENE_EXTENT_M,
  seed: number = 42,
): TerrainHeightmap {
  const data = new Float32Array(res * res);
  const rand = mulberry32(seed);
  const noise = makeValueNoise2D(rand, 8); // 8x8 grid of random values, smoothly interpolated

  const basinDepth = 5.0; // metres below water_level
  const basinRadius = 300.0; // metres (so diameter ~600 m as per brief)
  const landBase = 1.5; // metres above water level for typical land
  const edgeLift = 6.0; // additional rise at scene boundary

  for (let j = 0; j < res; j++) {
    const z = (j / (res - 1) - 0.5) * extent;
    for (let i = 0; i < res; i++) {
      const x = (i / (res - 1) - 0.5) * extent;
      const r = Math.hypot(x, z);

      // Gaussian basin (negative height = below water).
      const basin =
        -basinDepth * Math.exp(-(r * r) / (basinRadius * basinRadius));

      // Edge ramp (subtle; rises toward the scene boundary).
      const edge =
        edgeLift *
        Math.pow(Math.max(Math.abs(x), Math.abs(z)) / (extent * 0.5), 4);

      // Rolling hill noise on top of land base, scaled down inside the basin.
      const noiseAmp = 0.6; // metres
      const n = noise(x / 600, z / 600) * noiseAmp;

      // Soft mask: 0 inside basin, 1 outside (transitions smoothly across rim).
      const landMask = smoothstep(basinRadius * 0.85, basinRadius * 1.15, r);

      const land = (landBase + n + edge) * landMask;
      const h = basin + land;
      data[j * res + i] = h;
    }
  }

  return { data, resolution: res, extent };
}

function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** Tiny 2D value noise on an N×N lattice with smoothstep interpolation. */
function makeValueNoise2D(
  rand: () => number,
  n: number,
): (x: number, y: number) => number {
  const grid = new Float32Array((n + 1) * (n + 1));
  for (let i = 0; i < grid.length; i++) grid[i] = rand() * 2 - 1;
  return (x: number, y: number) => {
    const fx = ((x % 1) + 1) % 1;
    const fy = ((y % 1) + 1) % 1;
    const ix = Math.floor(fx * n);
    const iy = Math.floor(fy * n);
    const tx = fx * n - ix;
    const ty = fy * n - iy;
    const sx = tx * tx * (3 - 2 * tx);
    const sy = ty * ty * (3 - 2 * ty);
    const a = grid[iy * (n + 1) + ix]!;
    const b = grid[iy * (n + 1) + ix + 1]!;
    const c = grid[(iy + 1) * (n + 1) + ix]!;
    const d = grid[(iy + 1) * (n + 1) + ix + 1]!;
    const ab = a + (b - a) * sx;
    const cd = c + (d - c) * sx;
    return ab + (cd - ab) * sy;
  };
}

function smoothstep(edge0: number, edge1: number, x: number): number {
  const t = Math.max(0, Math.min(1, (x - edge0) / (edge1 - edge0)));
  return t * t * (3 - 2 * t);
}

/**
 * Upload a heightmap to a single-channel r32float texture.
 * Returns the GPUTexture (TEXTURE_BINDING usage; sample with non-filtering r32float sampler).
 */
export function uploadHeightmapTexture(
  device: GPUDevice,
  heightmap: TerrainHeightmap,
  label = "terrain.heightmap",
): GPUTexture {
  const tex = device.createTexture({
    label,
    size: { width: heightmap.resolution, height: heightmap.resolution },
    format: "r32float",
    usage: GPUTextureUsage.TEXTURE_BINDING | GPUTextureUsage.COPY_DST,
  });
  device.queue.writeTexture(
    { texture: tex },
    heightmap.data.buffer,
    {
      offset: heightmap.data.byteOffset,
      bytesPerRow: heightmap.resolution * 4,
      rowsPerImage: heightmap.resolution,
    },
    { width: heightmap.resolution, height: heightmap.resolution },
  );
  return tex;
}
